import { z } from 'zod';
import { ReferralPatientSchema } from '../ReferralPatientSchema';
import { ReferralLandgCaseManagerSchema } from './ReferralLandgCaseManagerSchema';
import { IObject } from '../../IObject';
import { ReferralBaseSchema } from '../ReferralBaseSchema';
import { ReferralLandgDocumentsSchema } from './ReferralLandgDocumentsSchema';
import { ReferralCoverageSchema } from '../ReferralCoverageSchema';
import { ReferralDiagnosisSchema } from '../ReferralDiagnosisSchema';
import {
  ReferralConsentRequestSchema,
  ReferralConsentSchema,
  requiresPatientPhoneForConsent,
} from '../ReferralConsentSchema';
import { ReferralNotesSchema } from '../ReferralNotesSchema';
import { allJoinReasonIds } from '../../../data/joinReasons';
import { joinReasonIdSchema } from '../ReferralJoinReasonSchema';
import { requiredTrimmedString } from '../../../validation';
import { withLegacyPlanIdentifiers } from '../hoistPlanIdentifiersToPatient';

const ReferralBaseLandgSchemaObject = {
  // ID is required for L&G referrals to be passed from UI
  // because a referrer uploads documents to the storage "referrals/{generated-referral-id}/document.pdf"
  id: z.string().min(1),
  patient: ReferralPatientSchema,
  coverage: ReferralCoverageSchema,
  diagnosis: ReferralDiagnosisSchema,
  joinReasonId: joinReasonIdSchema(allJoinReasonIds),
  caseManager: ReferralLandgCaseManagerSchema,
  // A missing `gip` is parsed as `{}` so the caller is told which field to
  // send (gip.code) rather than just "expected object, received undefined" —
  // what yup's cast-to-shape-defaults did.
  gip: z.preprocess(
    (value) => value ?? {},
    z.object({
      code: requiredTrimmedString('Case reference number is required'),
    }),
  ),
  documents: ReferralLandgDocumentsSchema,
  // Optional free text the case manager typed on the referral form. Internal
  // only: it is never shown to the member (see ReferralNotesSchema)
  notes: ReferralNotesSchema,
  // How we know the patient consented to being referred. The REQUEST form:
  // a body states the EVIDENCE only — the status and what we record when we
  // ask are ours to write (see ReferralConsentSchema; the stored schema
  // below carries the full shape)
  consent: ReferralConsentRequestSchema,
};

export const ReferralCreateLandgSchema = withLegacyPlanIdentifiers(
  z.object(ReferralBaseLandgSchemaObject).check(requiresPatientPhoneForConsent),
);

// As it is stored in the database
export const ReferralLandgSchema = ReferralBaseSchema.extend({
  ...ReferralBaseLandgSchemaObject,
  // as stored: the evidence and status the body stated, plus whatever we
  // recorded when we asked the patient ourselves
  consent: ReferralConsentSchema,
});

export type ReferralCreateLandg = z.infer<typeof ReferralCreateLandgSchema>;
export type ReferralLandgData = z.infer<typeof ReferralLandgSchema>;
export type ReferralLandg = IObject.HasIdTimestamped<ReferralLandgData>;
